import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/pages/home/components/SectionHeading";
import ReservationSteps from "./components/ReservationSteps";
import { Link } from "react-router-dom";
import { PHONE_NUMBER, PHONE_TEL } from "@/components/feature/navigation";
import BookingCalendar from "@/components/feature/BookingCalendar";
import type {
  BookingTexts,
  Timeslot,
  AppointmentPayload,
} from "@/components/feature/BookingCalendar";

const TIMESLOTS_URL =
  "https://readdy.ai/api/public/calendar/timeslots/31d92643-4eb2-404f-a8a2-92dd93b0e71b.978e7eed1300a6dd42fe73c0184e816f05777e7e24649f3eea5e6db42053c15c";
const APPOINTMENTS_URL =
  "https://readdy.ai/api/public/calendar/appointments/31d92643-4eb2-404f-a8a2-92dd93b0e71b.978e7eed1300a6dd42fe73c0184e816f05777e7e24649f3eea5e6db42053c15c";

const timeslotsApi = async (): Promise<Timeslot[]> => {
  const res = await fetch(TIMESLOTS_URL);
  if (!res.ok) throw new Error("timeslots fetch failed");
  return res.json();
};

const appointmentsApi = async (
  payload: AppointmentPayload
): Promise<unknown> => {
  const res = await fetch(APPOINTMENTS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("appointment create failed");
  return res.json();
};

const BOOKING_TEXTS: BookingTexts = {
  dateStepTitle: "날짜 선택",
  timeStepTitle: "시간 선택",
  reviewStepTitle: "정보 입력",
  dateHint: "예약 가능한 날짜만 선택할 수 있습니다.",
  timeHint: "원하시는 시간을 선택해주세요.",
  reviewHint: "예약 내용을 확인하고 정보를 입력해주세요.",
  next: "다음",
  prev: "이전",
  modifyTime: "시간 변경",
  submit: "예약 완료",
  submitting: "예약 중...",
  success: "예약이 완료되었습니다. 확인을 위해 연락드리겠습니다.",
  loading: "예약 가능한 시간을 불러오는 중입니다.",
  noSlots: "현재 예약 가능한 시간이 없습니다.",
  error: "요청을 처리하지 못했습니다. 잠시 후 다시 시도해주세요.",
  nameLabel: "성함",
  namePlaceholder: "이름을 입력해주세요",
  phoneLabel: "연락처",
  phonePlaceholder: "연락 가능한 전화번호를 입력해주세요",
  notesLabel: "요청 사항",
  notesPlaceholder: "피부 고민이나 특별히 원하시는 사항을 적어주세요",
  nameRequired: "이름을 입력해주세요.",
  phoneRequired: "연락처를 입력해주세요.",
  reviewDateLabel: "예약 날짜",
  reviewTimeLabel: "예약 시간",
  reviewNotesLabel: "요청 사항",
  weekdays: ["일", "월", "화", "수", "목", "금", "토"],
  monthNames: [
    "1월", "2월", "3월", "4월", "5월", "6월",
    "7월", "8월", "9월", "10월", "11월", "12월",
  ],
};

const INFO_ITEMS = [
  {
    icon: "ri-phone-line",
    title: "전화 상담",
    value: PHONE_NUMBER,
    href: PHONE_TEL,
  },
  {
    icon: "ri-time-line",
    title: "운영 시간",
    value: "평일 10:00 - 20:00 · 토 10:00 - 17:00",
  },
  {
    icon: "ri-map-pin-line",
    title: "찾아오시는 길",
    value: "서울특별시 강남구",
  },
];

export default function Reservation() {
  return (
    <>
      <PageHero
        eyebrow="Reservation"
        title="예약 상담"
        description="피부 고민과 목표를 충분히 나눈 뒤, 고객님께 꼭 맞는 관리를 시작합니다. 부담 없이 예약해주세요."
        image="https://readdy.ai/api/search-image?query=Soft%20elegant%20skincare%20reservation%20hero%20background%20with%20calendar%20flowers%20and%20delicate%20beauty%20products%20in%20dusty%20pink%20cream%20and%20mint%20tones%20minimalist%20serene%20spa%20atmosphere%20bright%20high%20key%20editorial%20aesthetic&width=1920&height=700&seq=bianca-reservation-hero&orientation=landscape"
      />

      {/* 예약 절차 */}
      <section className="w-full px-4 md:px-6 py-16 md:py-24">
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          <SectionHeading
            eyebrow="Process"
            title="예약부터 관리까지"
            description="간단한 4단계로 진행됩니다. 첫 방문 고객님을 위한 무료 피부 진단도 함께 제공해드립니다."
          />
          <ReservationSteps />
        </div>
      </section>

      {/* 온라인 예약 */}
      <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100/60">
        <div className="max-w-4xl mx-auto flex flex-col gap-10">
          <SectionHeading
            eyebrow="Booking"
            title="온라인 예약"
            description="아래 달력에서 원하는 날짜와 시간을 선택하고 예약을 완료해보세요."
          />
          <BookingCalendar
            timeslotsApi={timeslotsApi}
            appointmentsApi={appointmentsApi}
            texts={BOOKING_TEXTS}
          />
        </div>
      </section>

      {/* 연락처 안내 */}
      <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100/60">
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          <SectionHeading
            eyebrow="Contact"
            title="예약 및 상담 안내"
            description="원하시는 방법으로 편하게 문의해주세요. 친절하게 안내해드리겠습니다."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INFO_ITEMS.map((item) => {
              const content = (
                <div className="flex flex-col items-center gap-4 p-8 rounded-2xl bg-white border border-background-200/70 text-center transition-all hover:border-primary-200 cursor-pointer">
                  <span className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  </span>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-semibold text-foreground-900">{item.title}</span>
                    <span className="text-sm text-foreground-600 leading-relaxed">{item.value}</span>
                  </div>
                </div>
              );

              return item.href ? (
                <a key={item.title} href={item.href} className="block">
                  {content}
                </a>
              ) : (
                <div key={item.title}>{content}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 프로그램 안내 */}
      <section className="w-full px-4 md:px-6 py-16 md:py-24">
        <div className="max-w-6xl mx-auto flex flex-col gap-10">
          <SectionHeading
            eyebrow="Programs"
            title="어떤 관리가 필요하신가요?"
            description="6가지 전문 프로그램 중 고객님의 피부 고민에 맞는 관리를 확인해보세요."
          />
          <div className="flex justify-center">
            <Link
              to="/programs"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-white text-base font-semibold whitespace-nowrap transition-all hover:bg-primary-600 cursor-pointer"
            >
              관리 프로그램 보기
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}